import { api } from './api';
import { Entry, NoteEntry } from '../types';
import { entriesAddMany, entriesDeleteMany, entriesUpdateMany } from '../containers/contentSlice';
import { RootState } from '../store';

const noteTagId = (noteId: string) => `note:${noteId}`;
const locationTagId = (locationId: string) => `location:${locationId}`;

export interface LoadNoteResponseDto {
	note: NoteEntry;
	subRecordCount: number;
}

export interface LoadNotesAtLocationResponseDto {
	notes: LoadNoteResponseDto[];
}

export const notesApi = api.injectEndpoints({
	endpoints: (builder) => ({
		createNote: builder.mutation<Entry, NoteEntry>({
			query: (note: NoteEntry) => ({
				url: 'notes',
				method: 'POST',
				body: {
					...note,
					isDraft: undefined,
				},
			}),
			async onQueryStarted(note, { dispatch }) {
				dispatch(entriesAddMany([{ ...note, isDraft: true }]));
			},
			invalidatesTags: (_result, _error, note) => {
				const tags: Array<{ type: 'Notes'; id: string }> = [];
				const parentId = String(note.parentId ?? '');
				if (parentId) {
					tags.push({ type: 'Notes', id: noteTagId(parentId) });
				}
				return tags;
			},
		}),
		updateNote: builder.mutation<void, { id: string; patch: Partial<NoteEntry> }>({
			query: ({ id, patch }) => ({
				url: `notes/${id}`,
				method: 'PUT',
				body: {
					...patch,
					isDraft: undefined,
				},
			}),
			async onQueryStarted({ id, patch }, { dispatch, getState, queryFulfilled }) {
				const previousEntry = (getState() as RootState).content.entries.find(
					(entry) => entry.id === id,
				) as NoteEntry | undefined;
				const previousParentId = String(previousEntry?.parentId ?? '');
				const nextParentId = String(patch.parentId ?? '');
				dispatch(entriesUpdateMany([{ id, ...patch }]));
				try {
					await queryFulfilled;
					const parentIdsToInvalidate = new Set<string>();
					if (previousParentId) {
						parentIdsToInvalidate.add(previousParentId);
					}
					if (nextParentId) {
						parentIdsToInvalidate.add(nextParentId);
					}
					if (parentIdsToInvalidate.size) {
						dispatch(api.util.invalidateTags(
							Array.from(parentIdsToInvalidate).map((parentId) => ({
								type: 'Notes' as const,
								id: noteTagId(parentId),
							})),
						));
					}
				} catch {
					// no-op
				}
			},
			invalidatesTags: (_result, _error, arg) => [{ type: 'Notes' as const, id: noteTagId(arg.id) }],
		}),
		deleteNote: builder.mutation<{ deletedIds: string[] }, { id: string; parentId?: string | null }>({
			query: ({ id }) => ({
				url: `notes/${id}`,
				method: 'DELETE',
			}),
			async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
				try {
					const { data } = await queryFulfilled;
					dispatch(entriesDeleteMany(data.deletedIds ?? []));
				} catch {
					// no-op
				}
			},
			invalidatesTags: (_result, _error, arg) => {
				const tags: Array<{ type: 'Notes'; id: string }> = [];
				const parentId = String(arg.parentId ?? '');
				if (parentId) {
					tags.push({ type: 'Notes', id: noteTagId(parentId) });
				}
				return tags;
			},
		}),
		loadNote: builder.query<LoadNoteResponseDto, string>({
			query: (id: string) => ({
				url: `notes/${id}/load`,
				method: 'GET',
			}),
			providesTags: (_result, _error, id) => [{ type: 'Notes' as const, id: noteTagId(id) }],
			extraOptions: { maxRetries: 0 },
		}),
		loadNotesAtLocation: builder.query<LoadNotesAtLocationResponseDto, string>({
			query: (locationId: string) => ({
				url: 'notes/load',
				params: {
					locationId,
				},
				method: 'GET',
			}),
			async onQueryStarted(_locationId, { dispatch, queryFulfilled }) {
				try {
					const { data } = await queryFulfilled;
					for (const noteBundle of data.notes) {
						dispatch(entriesUpdateMany([noteBundle.note]));
						dispatch(
							(api.util.upsertQueryData as (
								endpointName: 'loadNote',
								arg: string,
								value: LoadNoteResponseDto,
							) => ReturnType<typeof api.util.upsertQueryData>)(
								'loadNote',
								noteBundle.note.id,
								noteBundle,
							),
						);
					}
				} catch {
					// ignore warmup failures; regular per-card queries still work
				}
			},
			providesTags: (_result, _error, locationId) => [
				{ type: 'Notes' as const, id: locationTagId(locationId) },
			],
			extraOptions: { maxRetries: 0 },
		}),
	}),
});

export const {
	useCreateNoteMutation,
	useUpdateNoteMutation,
	useDeleteNoteMutation,
	useLoadNoteQuery,
	useLoadNotesAtLocationQuery,
} = notesApi;
